import { useCallback } from 'react';
import { EAttributeType } from '../../../@types/AttributeTypes';
import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import { Side } from '../../../@types/Side';
import { snapRadiatorToWall } from '../../../components/three/Radiator';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import { IWall } from '../../../models/abstract/Wall';
import AttributeModel from '../../../models/standard/AttributeModel';
import PartitionWallModel from '../../../models/standard/PartitionWallModel';
import RadiatorModel from '../../../models/standard/RadiatorModel';
import StandardWallModel from '../../../models/standard/StandardWallModel';
import { DefaultAttributeId, getDefaultAttributesForObject, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';

export interface IRadiatorActions {
  deleteRadiator: (wallIndex: number, side: Side, radiatorIndex: number) => void;
  updateRadiatorWidth: (wallIndex: number, side: Side, radiatorIndex: number, newWidth: number) => void;
  updateRadiatorThickness: (wallIndex: number, side: Side, radiatorIndex: number, newThickness: number) => void;
  updateRadiatorNicheDepth: (wallIndex: number, side: Side, radiatorIndex: number, newNicheDepth: number) => void;
  updateRadiatorAttributes: (wallIndex: number, side: Side, radiatorIndex: number, attributes: AttributeModel[]) => void;
  setRadiatorDepthAttribute: (wallIndex: number, side: Side, radiatorIndex: number, depth: number, depthUnit: string | null) => void;
  setRadiatorLengthAttribute: (wallIndex: number, side: Side, radiatorIndex: number, length: number, lengthUnit: string | null) => void;
}

export const useRadiatorActions = (setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>): IRadiatorActions => {
  
  // --- HELPER: Reconstructs either wall type with updated radiators ---
  const recreateWallWithRadiators = (originalWall: IWall, side: Side, newRadiatorsForSide: RadiatorModel[]): IWall | null => {
    // 1. Get the "Other" side's radiators (to preserve them)
    const radsA = side === 'A' ? newRadiatorsForSide : (originalWall.sides?.A?.radiators || []);
    const radsB = side === 'B' ? newRadiatorsForSide : (originalWall.sides?.B?.radiators || []);

    if (originalWall instanceof StandardWallModel) {
      const newWall = new StandardWallModel(
        originalWall.startPoint,
        originalWall.endPoint,
        originalWall.depth,
        originalWall.windows,
        originalWall.doors,
        [], // Legacy radiators (empty)
        originalWall.attributes,
        originalWall.uuid,
        originalWall.passages,
      );
      // Assign sides
      newWall.sides.A.radiators = radsA;
      newWall.sides.B.radiators = radsB;
      newWall.updateWallLengths();
      return newWall;
    }

    if (originalWall instanceof PartitionWallModel) {
      const newWall = new PartitionWallModel(originalWall.startPoint, originalWall.endPoint);
      newWall.depth = originalWall.depth;
      newWall.windows = originalWall.windows;
      newWall.doors = originalWall.doors;
      newWall.passages = originalWall.passages;
      newWall.attributes = originalWall.attributes;
      newWall.setUUID(originalWall.uuid);

      // Assign sides (ensure structure exists)
      newWall.sides.A.radiators = radsA;
      newWall.sides.B.radiators = radsB;

      if (newWall.updateWallLengths) {
        newWall.updateWallLengths();
      }
      return newWall;
    }

    return null;
  };

  const deleteRadiator = useCallback(
    (wallIndex: number, side: Side, radiatorIndex: number): void => {
      setBuilding((prevBuilding) => {
        const walls = [...prevBuilding.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.sides || !currentWall.sides[side]) return prevBuilding;

        const updatedRadiators = [...currentWall.sides[side].radiators];
        updatedRadiators.splice(radiatorIndex, 1);

        const newWall = recreateWallWithRadiators(currentWall, side, updatedRadiators);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prevBuilding, isSetByUndoRedo: false, lastAction: EHistoryAction.RADIATOR_DELETED, walls };
        }
        return prevBuilding;
      });
    },
    [setBuilding],
  );

  const updateRadiatorWidth = useCallback(
    (wallIndex: number, side: Side, radiatorIndex: number, newWidth: number): void => {
      setBuilding((prevBuilding) => {
        const walls = [...prevBuilding.walls];
        const currentWall = walls[wallIndex];
        if (!currentWall || !currentWall.sides) return prevBuilding;

        const updatedRadiators = [...currentWall.sides[side].radiators];
        const currentRadiator = updatedRadiators[radiatorIndex];
        
        let newPos = currentRadiator.position;
        if (currentWall instanceof StandardWallModel) {
            newPos = snapRadiatorToWall(currentWall, currentRadiator.position, newWidth, currentRadiator.depth, currentRadiator.nicheDepth ?? 0, side);
        }

        const updatedRadiator = new RadiatorModel(
          newPos,
          newWidth,
          currentRadiator.depth,
          currentRadiator.angle,
          currentRadiator.attributes ?? [],
          undefined,
          currentRadiator.nicheDepth
        );
        updatedRadiator.setUUID(currentRadiator.uuid);
        updatedRadiators[radiatorIndex] = updatedRadiator;

        const newWall = recreateWallWithRadiators(currentWall, side, updatedRadiators);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prevBuilding, isSetByUndoRedo: false, lastAction: EHistoryAction.RADIATOR_WIDTH_CHANGED, walls };
        }
        return prevBuilding;
      });
    },
    [setBuilding],
  );

  const updateRadiatorThickness = useCallback(
    (wallIndex: number, side: Side, radiatorIndex: number, newThickness: number): void => {
      setBuilding((prevBuilding) => {
        const walls = [...prevBuilding.walls];
        const currentWall = walls[wallIndex];
        if (!currentWall || !currentWall.sides) return prevBuilding;

        const updatedRadiators = [...currentWall.sides[side].radiators];
        const currentRadiator = updatedRadiators[radiatorIndex];

        let newPos = currentRadiator.position;
        if (currentWall instanceof StandardWallModel) {
            newPos = snapRadiatorToWall(currentWall, currentRadiator.position, currentRadiator.length, newThickness, currentRadiator.nicheDepth ?? 0, side);
        }

        const updatedRadiator = new RadiatorModel(
          newPos,
          currentRadiator.length,
          newThickness,
          currentRadiator.angle,
          currentRadiator.attributes ?? [],
          undefined,
          currentRadiator.nicheDepth
        );
        updatedRadiator.setUUID(currentRadiator.uuid);
        updatedRadiators[radiatorIndex] = updatedRadiator;

        const newWall = recreateWallWithRadiators(currentWall, side, updatedRadiators);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prevBuilding, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.RADIATOR_THICKNESS_CHANGED };
        }
        return prevBuilding;
      });
    },
    [setBuilding],
  );

  const updateRadiatorNicheDepth = useCallback(
    (wallIndex: number, side: Side, radiatorIndex: number, newNicheDepth: number): void => {
      setBuilding((prevBuilding) => {
        const walls = [...prevBuilding.walls];
        const currentWall = walls[wallIndex];
        if (!currentWall || !currentWall.sides) return prevBuilding;

        const updatedRadiators = [...currentWall.sides[side].radiators];
        const currentRadiator = updatedRadiators[radiatorIndex];

        let newPos = currentRadiator.position;
        if (currentWall instanceof StandardWallModel) {
            newPos = snapRadiatorToWall(currentWall, currentRadiator.position, currentRadiator.length, currentRadiator.depth, newNicheDepth, side);
        }

        const updatedRadiator = new RadiatorModel(
          newPos,
          currentRadiator.length,
          currentRadiator.depth,
          currentRadiator.angle,
          currentRadiator.attributes ?? [],
          undefined,
          newNicheDepth
        );
        updatedRadiator.setUUID(currentRadiator.uuid);
        updatedRadiators[radiatorIndex] = updatedRadiator;

        const newWall = recreateWallWithRadiators(currentWall, side, updatedRadiators);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prevBuilding, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.RADIATOR_THICKNESS_CHANGED };
        }
        return prevBuilding;
      });
    },
    [setBuilding],
  );

  const updateRadiatorAttributes = useCallback(
    (wallIndex: number, side: Side, radiatorIndex: number, attributes: AttributeModel[]): void => {
      setBuilding((prevBuilding) => {
        const walls = [...prevBuilding.walls];
        const currentWall = walls[wallIndex];
        if (!currentWall || !currentWall.sides) return prevBuilding;

        const updatedRadiators = [...currentWall.sides[side].radiators];
        const currentRadiator = updatedRadiators[radiatorIndex];
        
        const defaultRadiatorAttrs = getDefaultAttributesForObject(ObjectType.Radiator);
        const updatedAttrs = attributes.map((attr) => {
          const defaultAttr = defaultRadiatorAttrs.find((def) => def.id === attr.id);
          return new AttributeModel(
            attr.id,
            attr.key,
            attr.value,
            attr.predecessorId,
            attr.type,
            attr.unit,
            attr.hidden ?? defaultAttr?.hidden ?? false,
            attr.forced ?? defaultAttr?.forced ?? false,
            attr.ownerId,
            attr.userId,
            attr.publicFlag,
            attr.localization,
            attr.defaults,
            attr.validation,
            attr.unitFamily
          );
        });

        const updatedRadiator = new RadiatorModel(
          currentRadiator.position,
          currentRadiator.length,
          currentRadiator.depth,
          currentRadiator.angle,
          updatedAttrs,
          undefined,
          currentRadiator.nicheDepth
        );
        updatedRadiator.setUUID(currentRadiator.uuid);
        updatedRadiators[radiatorIndex] = updatedRadiator;

        const newWall = recreateWallWithRadiators(currentWall, side, updatedRadiators);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prevBuilding, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.RADIATOR_ATTRIBUTES_UPDATED };
        }
        return prevBuilding;
      });
    },
    [setBuilding],
  );

  const setRadiatorDepthAttribute = useCallback(
    (wallIndex: number, side: Side, radiatorIndex: number, depth: number, depthUnit: string | null): void => {
      setBuilding((prevBuilding) => {
        const walls = [...prevBuilding.walls];
        const currentWall = walls[wallIndex];
        if (!currentWall || !currentWall.sides) return prevBuilding;

        const updatedRadiators = [...currentWall.sides[side].radiators];
        const currentRadiator = updatedRadiators[radiatorIndex];

        if (!currentRadiator?.attributes?.some((attr) => attr.id === DefaultAttributeId.RADIATOR_DEPTH)) {
          return prevBuilding;
        }

        const updatedAttrs = currentRadiator.attributes.map((attr) => {
          if (attr.id === DefaultAttributeId.RADIATOR_DEPTH) {
            return new AttributeModel(
              attr.id, attr.key, depth, attr.predecessorId, EAttributeType.NUMBER, depthUnit, attr.hidden, attr.forced,
              attr.ownerId, attr.userId, attr.publicFlag, attr.localization, attr.defaults, attr.validation, attr.unitFamily
            );
          }
          return attr;
        });

        const updatedRadiator = new RadiatorModel(
          currentRadiator.position,
          currentRadiator.length,
          currentRadiator.depth,
          currentRadiator.angle,
          updatedAttrs,
          undefined,
          currentRadiator.nicheDepth
        );
        updatedRadiator.setUUID(currentRadiator.uuid);
        updatedRadiators[radiatorIndex] = updatedRadiator;

        const newWall = recreateWallWithRadiators(currentWall, side, updatedRadiators);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prevBuilding, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.RADIATOR_DEPTH_ATTRIBUTES_UPDATED };
        }
        return prevBuilding;
      });
    },
    [setBuilding],
  );

  const setRadiatorLengthAttribute = useCallback(
    (wallIndex: number, side: Side, radiatorIndex: number, length: number, lengthUnit: string | null): void => {
      setBuilding((prevBuilding) => {
        const walls = [...prevBuilding.walls];
        const currentWall = walls[wallIndex];
        if (!currentWall || !currentWall.sides) return prevBuilding;

        const updatedRadiators = [...currentWall.sides[side].radiators];
        const currentRadiator = updatedRadiators[radiatorIndex];

        if (!currentRadiator?.attributes?.some((attr) => attr.id === DefaultAttributeId.RADIATOR_LENGTH)) {
          return prevBuilding;
        }

        const updatedAttrs = currentRadiator.attributes.map((attr) => {
          if (attr.id === DefaultAttributeId.RADIATOR_LENGTH) {
            return new AttributeModel(
              attr.id, attr.key, length, attr.predecessorId, EAttributeType.NUMBER, lengthUnit, attr.hidden, attr.forced,
              attr.ownerId, attr.userId, attr.publicFlag, attr.localization, attr.defaults, attr.validation, attr.unitFamily
            );
          }
          return attr;
        });

        const updatedRadiator = new RadiatorModel(
          currentRadiator.position,
          currentRadiator.length,
          currentRadiator.depth,
          currentRadiator.angle,
          updatedAttrs,
          undefined,
          currentRadiator.nicheDepth
        );
        updatedRadiator.setUUID(currentRadiator.uuid);
        updatedRadiators[radiatorIndex] = updatedRadiator;

        const newWall = recreateWallWithRadiators(currentWall, side, updatedRadiators);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prevBuilding, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.RADIATOR_LENGHT_ATTRIBUTES_UPDATED };
        }
        return prevBuilding;
      });
    },
    [setBuilding],
  );

  return {
    deleteRadiator,
    updateRadiatorWidth,
    updateRadiatorThickness,
    updateRadiatorNicheDepth,
    updateRadiatorAttributes,
    setRadiatorDepthAttribute,
    setRadiatorLengthAttribute,
  };
};
